import { useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useContarVisita } from "~/lib/hooks/useContarVisita";

const NAV = [
  { to: "/ia", label: "Assuntos" },
  { to: "/tutorial", label: "Tutoriais" },
  { to: "/build", label: "Trabalho" },
  { to: "/hire", label: "Contato" },
] as const;

// A data do build, injetada pelo Vite. Mostrar quando o site foi
// atualizado é o tipo de detalhe que separa site mantido de site parado.
declare const __BUILD_DATE__: string;

function atualizadoEm() {
  const d = new Date(
    typeof __BUILD_DATE__ === "string" ? __BUILD_DATE__ : Date.now(),
  );
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function Root() {
  const { pathname, hash } = useLocation();
  useContarVisita();

  // Sobe para o topo a cada troca de rota, para a tela nova abrir pelo
  // cabeçalho dela. Com hash, deixa o link de âncora fazer o trabalho.
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);

  return (
    <div className="flex min-h-screen flex-col bg-jg-bg text-jg-fg">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-jg-accent focus:px-4 focus:py-2 focus:text-jg-bg"
      >
        Pular para o conteúdo
      </a>

      <header className="border-b border-jg-rule">
        <div className="jg-shell flex items-baseline justify-between gap-6 py-5">
          <Link
            to="/"
            className="font-display text-xl font-semibold tracking-tight"
            aria-label="JGson, página inicial"
          >
            JGson
          </Link>

          <nav aria-label="Principal" className="flex items-baseline gap-5 sm:gap-7">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    "text-sm transition-colors",
                    isActive
                      ? "text-jg-fg underline decoration-jg-accent decoration-2 underline-offset-[6px]"
                      : "text-jg-fg-muted hover:text-jg-fg",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main id="conteudo" className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-32 border-t border-jg-rule">
        <div className="jg-shell grid gap-6 py-12 sm:grid-cols-[2fr_1fr]">
          <div>
            <p className="font-display text-lg">JGson</p>
            <p className="mt-1 max-w-sm text-sm leading-relaxed text-jg-fg-muted">
              Estúdio de uma pessoa só: João Vidotto, engenheiro de software há
              dez anos. Explicações de inteligência artificial, tutoriais e
              software sob medida.
            </p>
          </div>

          <div className="text-sm">
            <p className="jg-eyebrow">Onde me achar</p>
            <ul className="mt-3 space-y-1.5">
              <li>
                <a className="jg-link" href="mailto:joao@jgson.net">
                  joao@jgson.net
                </a>
              </li>
              <li>
                <a
                  className="jg-link"
                  href="https://instagram.com/bittarg"
                  rel="me noopener"
                  target="_blank"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a
                  className="jg-link"
                  href="https://www.tiktok.com/@jgvidotto"
                  rel="me noopener"
                  target="_blank"
                >
                  TikTok
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="jg-shell flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 pb-10 text-xs text-jg-fg-muted">
          <span>
            © {new Date().getFullYear()} JGson ·{" "}
            <Link className="jg-link" to="/privacidade">
              privacidade
            </Link>
          </span>
          <span className="font-mono tabular-nums">
            última atualização {atualizadoEm()}
          </span>
        </div>
      </footer>
    </div>
  );
}
